import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, X, ChevronRight, Megaphone } from "lucide-react";
import { playChime } from "@/lib/chime";

export default function NotificationBanner() {
  const [items, setItems] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]); // active broadcasts (rotate)
  const [broadcastIdx, setBroadcastIdx] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  const [chimeStyle, setChimeStyle] = useState(() => {
    try { return window.localStorage.getItem("notif:chime-style") || "ping"; } catch { return "ping"; }
  });
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    try { return window.localStorage.getItem("notif:chime") !== "off"; } catch { return true; }
  });
  const navigate = useNavigate();
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = async () => {
    try {
      const [notifResp, banResp] = await Promise.all([
        api.get("/api/notifications"),
        api.get("/api/settings/broadcast-banners").catch(() => ({ data: null })),
      ]);
      const sevenDaysAgo = Date.now() - 7 * 86400_000;
      const fresh = (notifResp.data || []).filter((n) => {
        if (n.read) return false;
        const t = n.created_at ? new Date(n.created_at).getTime() : 0;
        return t >= sevenDaysAgo;
      });
      const newIds = fresh.map((n) => n.id).filter((id) => !seenIdsRef.current.has(id));
      if (!firstLoadRef.current && newIds.length > 0 && chimeEnabled) playChime(chimeStyle);
      seenIdsRef.current = new Set(fresh.map((n) => n.id));
      firstLoadRef.current = false;
      setItems(fresh);
      const active = ((banResp?.data?.banners) || []).filter((b) => b.active);
      setBroadcasts(active);
      setBroadcastIdx((i) => (active.length ? i % active.length : 0));
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    const onStorage = (e) => {
      if (e.key === "notif:chime") setChimeEnabled(e.newValue !== "off");
      if (e.key === "notif:chime-style" && e.newValue) setChimeStyle(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => { clearInterval(id); window.removeEventListener("storage", onStorage); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotate the active broadcast every 8s so ops + festive + generic can share the slot
  useEffect(() => {
    if (broadcasts.length <= 1) return;
    const t = setInterval(() => setBroadcastIdx((i) => (i + 1) % broadcasts.length), 8000);
    return () => clearInterval(t);
  }, [broadcasts.length]);

  const markAllRead = async () => {
    setDismissing(true);
    try {
      await Promise.all(items.map((n) => api.post(`/api/notifications/${n.id}/read`).catch(() => {})));
      setItems([]);
    } finally { setDismissing(false); }
  };

  const latest = items[0];
  const rest = Math.max(items.length - 1, 0);
  const activeBroadcast = broadcasts[broadcastIdx];

  if (!items.length && !activeBroadcast) return null;

  return (
    <div className="sticky top-0 z-30 flex flex-col" data-testid="unread-banner-wrap">
      <style>{`
        @keyframes ts-marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .ts-marquee { display: inline-block; white-space: nowrap; will-change: transform; animation: ts-marquee 26s linear infinite; }
        .ts-marquee-wrap:hover .ts-marquee { animation-play-state: paused; }
        .ts-marquee-wrap { overflow: hidden; flex: 1; min-width: 0; }
      `}</style>
      {activeBroadcast && (
        <div
          className={`border-b px-4 py-2 flex items-center gap-3 backdrop-blur-md ${
            activeBroadcast.kind === "warning"
              ? "border-orange-700/40 bg-orange-600/95 text-white"
              : "border-slate-800/30 bg-slate-800/95 text-white"
          }`}
          data-testid="broadcast-banner"
        >
          <Megaphone className="w-4 h-4 shrink-0" />
          <b className="shrink-0">Announcement</b>
          <div className="ts-marquee-wrap" title="Hover to pause">
            <span className="ts-marquee text-sm text-white/95" data-testid="broadcast-marquee">
              {activeBroadcast.message}
            </span>
          </div>
          {broadcasts.length > 1 && (
            <span className="text-[10px] bg-white/20 rounded-full px-1.5 py-0.5 whitespace-nowrap shrink-0"
                  data-testid="broadcast-rotate-counter">
              {broadcastIdx + 1}/{broadcasts.length}
            </span>
          )}
        </div>
      )}
      {latest && (
        <div
          className="border-b border-brand/40 bg-brand/95 text-white px-4 py-2 flex items-center gap-3 backdrop-blur-md"
          data-testid="unread-banner"
        >
          <Bell className="w-4 h-4 shrink-0" />
          <div className="ts-marquee-wrap" title="Hover to pause">
            <span className="ts-marquee text-sm cursor-pointer" data-testid="unread-marquee"
                  onClick={() => { if (latest.link) navigate(latest.link); else navigate("/notifications"); }}>
              <b className="mr-2">{latest.title}</b>
              <span className="text-white/90">— {latest.message}</span>
              {rest > 0 && <span className="ml-2 text-white/90">· +{rest} more unread</span>}
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/notifications")}
            className="text-xs underline hover:opacity-90 whitespace-nowrap shrink-0"
            data-testid="unread-banner-view-all"
          >
            View all
          </button>
          <button
            type="button"
            onClick={markAllRead}
            disabled={dismissing}
            className="inline-flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 whitespace-nowrap disabled:opacity-60 shrink-0"
            data-testid="unread-banner-dismiss"
            title="Mark all shown notifications as read"
          >
            <X className="w-3 h-3" /> Mark all read
          </button>
        </div>
      )}
    </div>
  );
}
